import React, { useState } from 'react'
import { graphql, useFragment } from 'react-relay'

import { Navigate } from 'react-router-dom'

import { useMutation } from '@data/useMutation'

import { GuildDonationAmountForm } from './GuildDonationAmountForm'
import { GuildDonationPaymentForm } from './GuildDonationPaymentForm'

import { GuildDonationScreenContent_guild$key } from './__generated__/GuildDonationScreenContent_guild.graphql'
import { GuildDonationScreenContentCreateMutation } from './__generated__/GuildDonationScreenContentCreateMutation.graphql'
import { GuildDonationScreenContentUpdateAmountMutation } from './__generated__/GuildDonationScreenContentUpdateAmountMutation.graphql'
import { GuildDonationScreenContentCreateDlocalGoMutation } from './__generated__/GuildDonationScreenContentCreateDlocalGoMutation.graphql'

export const GuildDonationScreenContent = ({
  guild,
}: {
  guild: GuildDonationScreenContent_guild$key
}) => {
  const data = useFragment(
    graphql`
      fragment GuildDonationScreenContent_guild on Guild {
        id
        slugId

        minimumDonationAmount
        donationCurrency
        stripeAccountId
        paymentProvider
      }
    `,
    guild
  )

  const [donationAmount, setDonationAmount] = useState<number>()
  const [formStep, setFormStep] = useState<'amount' | 'payment'>('amount')
  const [paymentIntentData, setPaymentIntentData] = useState<{
    paymentIntentId: string | null | undefined
    clientSecret: string | null | undefined
  }>()

  const createPaymentIntent =
    useMutation<GuildDonationScreenContentCreateMutation>(graphql`
      mutation GuildDonationScreenContentCreateMutation(
        $guildId: ID!
        $amount: Int!
      ) {
        createPaymentIntent(guildId: $guildId, amount: $amount) {
          paymentIntentId
          clientSecret
          feesPayer
        }
      }
    `)

  const updatePaymentIntentAmount =
    useMutation<GuildDonationScreenContentUpdateAmountMutation>(graphql`
      mutation GuildDonationScreenContentUpdateAmountMutation(
        $guildId: ID!
        $paymentIntentId: String!
        $amount: Int!
      ) {
        updatePaymentIntentAmount(
          guildId: $guildId
          paymentIntentId: $paymentIntentId
          amount: $amount
        ) {
          paymentIntentId
          clientSecret
          feesPayer
        }
      }
    `)

  const createDlocalGoDonation =
    useMutation<GuildDonationScreenContentCreateDlocalGoMutation>(graphql`
      mutation GuildDonationScreenContentCreateDlocalGoMutation(
        $guildId: ID!
        $amount: Int!
      ) {
        createDlocalGoDonation(guildId: $guildId, amount: $amount) {
          orderId
          redirectUrl
        }
      }
    `)

  const usesDlocalGo = data.paymentProvider === 'DLOCAL_GO'

  if (!data.minimumDonationAmount || !data.donationCurrency) {
    return <Navigate to={`/${data.slugId}`} />
  }

  if (formStep === 'amount' || !paymentIntentData?.clientSecret) {
    return (
      <GuildDonationAmountForm
        minimumDonationAmount={data.minimumDonationAmount}
        defaultDonationAmount={donationAmount}
        donationCurrency={usesDlocalGo ? 'BRL' : data.donationCurrency}
        onSubmit={async (donationAmount) => {
          setDonationAmount(donationAmount)

          // Brazilian guilds take donations through dLocal Go's checkout
          if (usesDlocalGo) {
            const response = await createDlocalGoDonation({
              variables: { guildId: data.id, amount: donationAmount },
            })

            window.location.assign(response.createDlocalGoDonation.redirectUrl)
            return
          }

          if (!paymentIntentData || !paymentIntentData.paymentIntentId) {
            const response = await createPaymentIntent({
              variables: { guildId: data.id, amount: donationAmount },
            })

            setPaymentIntentData(response.createPaymentIntent)
            setFormStep('payment')
          } else if (paymentIntentData.paymentIntentId) {
            const response = await updatePaymentIntentAmount({
              variables: {
                guildId: data.id,
                paymentIntentId: paymentIntentData.paymentIntentId,
                amount: donationAmount,
              },
            })

            setPaymentIntentData(response.updatePaymentIntentAmount)
            setFormStep('payment')
          }
        }}
      />
    )
  }

  return (
    <GuildDonationPaymentForm
      clientSecret={paymentIntentData.clientSecret}
      backToAmountForm={() => {
        setFormStep('amount')
      }}
      guildSlugId={data.slugId}
      stripeAccountId={data.stripeAccountId}
    />
  )
}
